import { ComponentType } from 'react';
import { Palette, Theme } from '@mui/material';
import { resolveDomain } from 'libs/domain';
import { ErrorHandlerProvider } from 'libs/error';

interface ApiConfig {
  readonly apiUrl: string;
}

interface AuthConfig {
  readonly region: string;
  readonly userPoolId: string;
  readonly userPoolClientId: string;
}

interface AxonConfig {
  readonly axonUrl: string;
}

interface OAuthConfig {
  readonly redirectUrl: string;
}

interface MuiXConfig {
  readonly licenseKey: string;
}

interface SentryConfig {
  readonly isEnabled: boolean;
  readonly orgId: string;
  readonly dsn: string;
}

interface FullstoryConfig {
  readonly isEnabled: boolean;
  readonly orgId: string;
}

interface AppcuesConfig {
  readonly isEnabled: boolean;
}

interface MagicBellConfig {
  readonly isEnabled: boolean;
  readonly apiKey: string;
}

interface DapConfig {
  dashboardFilterPresetVersion: string;
}

interface TexConfig {
  chatUrl: string;
}

interface CreativePreviewConfig {
  readonly apiUrl: string;
  readonly shareLinkUrl: string;
}

interface FeatureFlagsConfig extends Record<string, boolean> {
  forceShowNativeDashboards: boolean;
  campaignReportingAIDataExport: boolean;
}

export type SVGComponentType = ComponentType<{
  width?: number | string;
  height?: number | string;
}>;

export type DomainAssets = {
  logo: SVGComponentType;
  authBg: string;
};

// heatmap colors amd gradient are the same for everyone.
export type Palettes = {
  lightPalette: Palette;
  darkPalette: Palette;
  chartPalette: string[];
};

export interface DomainConfig {
  // e.g. "acme" in "acme.kortx.io", will be "localhost"
  // when no domain is matched
  readonly key: string;
  // e.g. 171, the client id for this domain
  // used to constrain users into using
  readonly clientId?: string;
  // holds assets that should change per domain.
  readonly assets: DomainAssets;
  // Charts use the palettes directly
  readonly palettes: Palettes;
}

export interface ThemedDomainConfig extends DomainConfig {
  readonly themes: Theme[];
  readonly isDefaultDomain: boolean;
}

interface GoogleConfig {
  mapsApiKey: string;
}

interface Config {
  readonly api: ApiConfig;
  readonly appcues: AppcuesConfig;
  readonly auth: AuthConfig;
  readonly axon: AxonConfig;
  readonly errorHandler: ErrorHandlerProvider;
  readonly google: GoogleConfig;
  readonly mui: MuiXConfig;
  readonly oauth: OAuthConfig;
  readonly s3BucketNameForPublicDocuments: string;
  readonly sentry: SentryConfig;
  readonly fullstory: FullstoryConfig;
  readonly magicBell: MagicBellConfig;
  readonly featureFlags: FeatureFlagsConfig;
  readonly dap: DapConfig;
  readonly tex: TexConfig;
  readonly creativePreview: CreativePreviewConfig;
  readonly domain: ThemedDomainConfig;
}

export const initConfig = (): Config => {
  const cognito = {
    region: import.meta.env.VITE_COGNITO_REGION,
    userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
    userPoolClientId: import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID,
    cookieStorageDomain: import.meta.env.VITE_COGNITO_COOKIE_STORAGE_DOMAIN,
  };

  return {
    api: {
      apiUrl: import.meta.env.VITE_API_URL,
    },
    appcues: {
      isEnabled: import.meta.env.VITE_ENABLE_APPCUES === 'true',
    },
    axon: {
      axonUrl: import.meta.env.VITE_AXON_URL,
    },
    oauth: {
      // TODO: the base URL will change for some domains, we shouldn't default to localhost
      redirectUrl: import.meta.env.VITE_OAUTH_REDIRECT_URL,
    },
    auth: {
      region: cognito.region,
      userPoolId: cognito.userPoolId,
      userPoolClientId: cognito.userPoolClientId,
    },
    mui: {
      licenseKey: import.meta.env.VITE_MUI_X_LICENSE_KEY,
    },
    google: {
      mapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    },
    sentry: {
      isEnabled:
        import.meta.env.VITE_ERROR_HANDLER === ErrorHandlerProvider.sentry,
      orgId: 'kortx',
      dsn: import.meta.env.VITE_SENTRY_DSN,
    },
    errorHandler:
      ErrorHandlerProvider[
        import.meta.env.VITE_ERROR_HANDLER as keyof typeof ErrorHandlerProvider
      ] ?? ErrorHandlerProvider.console,
    s3BucketNameForPublicDocuments: import.meta.env
      .VITE_S3_BUCKET_NAME_FOR_PUBLIC_DOCUMENTS,
    fullstory: {
      isEnabled: import.meta.env.VITE_ENABLE_FULLSTORY === 'true',
      orgId: '12G20J',
    },
    magicBell: {
      isEnabled: import.meta.env.VITE_ENABLE_MAGIC_BELL === 'true',
      apiKey: import.meta.env.VITE_MAGIC_BELL_API_KEY,
    },
    featureFlags: {
      // Can stay empty, add feature flags here to avoid cluttering the regular config space
      // example:
      // audiences: import.meta.env.VITE_FEATURE_AUDIENCES === 'true',
      forceShowNativeDashboards:
        import.meta.env.VITE_FEATURE_FORCE_SHOW_NATIVE_DASH === 'true',
      // This flag is off on undefined/empty
      // It's on if any string with length > 0 is passed.
      campaignReportingAIDataExport:
        import.meta.env.VITE_FEATURE_CAMPAIGN_REPORTING_AI_DATA_EXPORT ===
        'true',
    },
    dap: {
      dashboardFilterPresetVersion:
        import.meta.env.VITE_DAP_FILTER_PRESET_VERSION || 'v1',
    },
    tex: {
      chatUrl: import.meta.env.VITE_TEX_CHAT_URL || 'https://kortx.io',
    },
    creativePreview: {
      apiUrl: `${import.meta.env.VITE_CREATIVE_PREVIEW_API_URL}/api`,
      shareLinkUrl: `${import.meta.env.VITE_CREATIVE_PREVIEW_API_URL}/share-link`,
    },
    // libs/domain has a map of all domains with related resources/conventions.
    domain: resolveDomain(import.meta.env.VITE_ENABLE_CUSTOM_DOMAINS ?? ''),
  };
};

const config = initConfig();

export default config;
